import InvoiceFooter from "Components/Common/PdfPreview/InvoiceFooter";
import ItemsTable from "Components/Common/PdfPreview/ItemList";
import SenderAndBillingDetails from "Components/Common/PdfPreview/SenderAndBillingDetails";
import { memo, useMemo } from "react";
import { Col } from "reactstrap";
import { CompanyPayload } from "types/auth.api";
import { ClientsPayloadData } from "types/client.api";
import { ItemData } from "types/item.api";
import { SectionPayloadData } from "types/section.api";
import parse from "html-react-parser";
// import { QuoteDetailsPayload } from "types/quote.api";

interface Props {
  companyDetails?: CompanyPayload | null;
  clientDetails?: ClientsPayloadData | null;
  selectedOption?: string;
  issueDate?: string;
  dueDate?: string;
  tableData?: ItemData[];
  referenceNumber?: string;
  selectedTab?: string;
  jobDescription?: string;
  allSelectedSections?: SectionPayloadData[];
  quoteTitle?: string;
  isCompanyPhoneHidden?: boolean;
  isCompanyNameHidden?: boolean;
}
const TemplateFirst = ({
  clientDetails,
  companyDetails,
  selectedOption,
  dueDate,
  issueDate,
  tableData = [],
  referenceNumber,
  selectedTab,
  quoteTitle,
  jobDescription,
  allSelectedSections,
  isCompanyPhoneHidden,
  isCompanyNameHidden,
}: Props) => {
  // -----------------------
  //  COLUMNS PER VIEW TYPE
  // -----------------------
  const columns = useMemo(() => {
    switch (selectedOption) {
      case "by-item":
        return [
          {
            header: "Item",
            accessorKey: "name",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "quantity",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit Price",
            accessorKey: "price",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const row = cell.row.original as ItemData;
              const qty = Number(row.quantity ?? 0);
              const price = Number(row.price ?? 0);
              const total = qty * price;

              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{total.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category":
        return [
          {
            header: "Category",
            accessorKey: "category",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-subcategory":
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category-subcategory-item":
      default:
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Item",
            accessorKey: "item",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "qty",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit Price",
            accessorKey: "unitPrice",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];
    }
  }, [selectedOption]);

  // -----------------------------------
  //  GROUPING / SUMMARY DATA PER MODE
  // -----------------------------------
  const { categoryViewData, subcategoryViewData, allViewData } = useMemo(() => {
    const result = {
      categoryViewData: [] as {
        category: string;
        itemsCount: number;
        total: number;
      }[],
      subcategoryViewData: [] as {
        subcategory: string;
        itemsCount: number;
        total: number;
      }[],
      allViewData: [] as {
        categoryId: string | number;
        category: string;
        rows: any[];
      }[],
    };

    if (!Array.isArray(tableData)) return result;

    const categoryMap = new Map<
      string | number,
      {
        categoryId: string | number;
        category: string;
        subMap: Map<
          string | number,
          {
            subcategoryId: string | number;
            subcategory: string;
            itemMap: Map<
              string,
              {
                name: string;
                qty: number;
                price: number;
                total: number;
                unit: string;
              }
            >;
          }
        >;
      }
    >();

    tableData.forEach((item) => {
      const categoryId = item.category_id ?? "na";
      const categoryName = item.category_name ?? "-";

      const subId = item.subcategory_id ?? "na";
      const subName = item.subcategory_name ?? "-";

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          categoryId,
          category: categoryName,
          subMap: new Map(),
        });
      }

      const categoryData = categoryMap.get(categoryId)!;

      if (!categoryData.subMap.has(subId)) {
        categoryData.subMap.set(subId, {
          subcategoryId: subId,
          subcategory: subName,
          itemMap: new Map(),
        });
      }

      const subData = categoryData.subMap.get(subId)!;

      const itemKey = item.name?.toLowerCase() ?? "unknown-item";
      const qty = Number(item.quantity ?? 0);
      const price = Number(item.price ?? 0);

      if (!subData.itemMap.has(itemKey)) {
        subData.itemMap.set(itemKey, {
          name: item.name,
          qty,
          price,
          total: qty * price,
          unit: item.unit,
        });
      } else {
        const exist = subData.itemMap.get(itemKey)!;
        exist.qty += qty;
        exist.total += qty * price;
      }
    });

    const categoryArray = Array.from(categoryMap.values());

    // CATEGORIES summary
    let catTemp = categoryArray.map((cat) => {
      const subItems = Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()),
      );
      return {
        category: cat.category,
        itemsCount: subItems.reduce((s, i) => s + i.qty, 0),
        total: subItems.reduce((s, i) => s + i.total, 0),
      };
    });

    // Merge - Categories
    const catNA = catTemp.filter((c) => c.category === "-");
    const catValid = catTemp.filter((c) => c.category !== "-");

    if (catNA.length > 0) {
      const merged = catNA.reduce(
        (acc, c) => {
          acc.itemsCount += c.itemsCount;
          acc.total += c.total;
          return acc;
        },
        { category: "-", itemsCount: 0, total: 0 },
      );
      result.categoryViewData = [...catValid, merged];
    } else {
      result.categoryViewData = catValid;
    }

    // SUBCATEGORY summary
    let subTemp = categoryArray.flatMap((cat) =>
      Array.from(cat.subMap.values()).map((sub) => {
        const items = Array.from(sub.itemMap.values());
        return {
          subcategory: sub.subcategory,
          itemsCount: items.reduce((s, i) => s + i.qty, 0),
          total: items.reduce((s, i) => s + i.total, 0),
        };
      }),
    );

    // Merge - Subcategories
    const subNA = subTemp.filter((s) => s.subcategory === "-");
    const subValid = subTemp.filter((s) => s.subcategory !== "-");

    if (subNA.length > 0) {
      const mergedSub = subNA.reduce(
        (acc, s) => {
          acc.itemsCount += s.itemsCount;
          acc.total += s.total;
          return acc;
        },
        { subcategory: "-", itemsCount: 0, total: 0 },
      );
      result.subcategoryViewData = [...subValid, mergedSub];
    } else {
      result.subcategoryViewData = subValid;
    }

    // ALL view (Category → Subcategory → Items)
    result.allViewData = categoryArray.map((cat) => ({
      categoryId: cat.categoryId,
      category: cat.category,
      rows: Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()).map((i) => ({
          subcategory: sub.subcategory,
          item: i.name,
          qty: i.qty,
          unitPrice: i.price,
          total: i.total,
          unit: i.unit,
        })),
      ),
    }));

    return result;
  }, [tableData]);

  // decide data for non-"all" views
  const mainTableData = useMemo(() => {
    switch (selectedOption) {
      case "by-category":
        return categoryViewData;
      case "by-subcategory":
        return subcategoryViewData;
      case "by-item":
      default:
        return tableData;
    }
  }, [selectedOption, categoryViewData, subcategoryViewData, tableData]);

  const brandColor = companyDetails?.brand_color ?? "#082B60";

  return (
    <Col
      className="position-relative p-0"
      style={{
        cursor: "pointer",
        borderRadius: "12px",
        minHeight: "100vh",
      }}
    >
      {/* <img
        src={ICONS.quote_blank_template1}
        alt="template"
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          objectFit: "cover",
          borderRadius: "12px",
          zIndex: 0,
        }}
      /> */}
      <svg
        width="145"
        height="100"
        viewBox="0 0 145 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="position-absolute top-0 end-0"
      >
        <g clip-path="url(#clip0_3964_9317)">
          <path
            d="M42.0062 74.7417L33.2748 66.0186L41.5928 57.7L42.0062 58.1133L42.0065 58.113L49.9081 66.0143L49.9038 66.0186L50.3212 66.4353L42.0062 74.7417Z"
            fill="#FEFFFF"
          />
          <path
            d="M8.73971 24.8549L0 16.1236L8.32202 7.80889L8.73971 8.22661L8.74439 8.22159L16.6417 16.1196L16.6377 16.1236L16.642 16.1279L8.32735 24.4422L8.74005 24.8546L8.73971 24.8549Z"
            fill="#F7F7F7"
          />
          <path
            d="M16.646 32.7526L8.73996 24.8546L8.32727 24.4422L16.6419 16.1279L24.9523 24.4382L16.646 32.7526Z"
            fill="#F7F7F7"
          />
          <path
            d="M25.3692 41.4926L16.6378 32.7609L16.6462 32.7526L24.9525 24.4383L24.9562 24.4419L24.9558 24.4422L25.3055 24.7912L25.3692 24.855L33.2818 32.7523L24.9558 41.0706L25.3739 41.4883L25.3692 41.4926Z"
            fill="#FCFCFC"
          />
          <path
            d="M33.278 49.3859L25.3737 41.4883L24.9557 41.0706L33.2817 32.7522L33.2824 32.7532L33.2747 32.7609L41.5927 41.0793L33.278 49.3859Z"
            fill="#FAFAFA"
          />
          <path
            d="M42.0062 58.1133L41.5928 57.6999L33.2748 49.3896L33.2781 49.3859L41.5928 41.0792L42.0062 41.4926L42.0108 41.4879L49.9078 49.3855L49.9038 49.3896L49.9081 49.3939L41.5935 57.6996L42.0065 58.1129L42.0062 58.1133Z"
            fill="#FAFAFA"
          />
          <path
            d="M49.9082 66.0143L42.0065 58.113L41.5935 57.6996L49.9082 49.3939L58.2259 57.6956L58.2219 57.6996L58.2262 57.7039L49.9082 66.0143Z"
            fill="#FEFFFF"
          />
          <path
            d="M58.23 74.3286L50.3211 66.4353L49.9037 66.0186L49.908 66.0143L58.226 57.704L66.5327 66.018L66.532 66.0186L58.23 74.3286Z"
            fill="#FEFFFF"
          />
          <path
            d="M66.9459 83.069L58.2222 74.337L58.2305 74.3286L66.5328 66.019L74.8509 74.3286L66.5325 82.647L66.9502 83.065L66.9459 83.069Z"
            fill="#FEFFFF"
          />
          <path
            d="M75.2641 91.383L66.95 83.065L66.5323 82.647L74.8507 74.3286L74.855 74.333L74.851 74.337L83.1657 82.652L74.851 90.9617L75.2684 91.3787L75.2641 91.383Z"
            fill="#FEFFFF"
          />
          <path
            d="M83.5826 99.6934L75.2682 91.3787L74.8508 90.9617L83.1655 82.652L83.5826 83.069L83.5872 83.0647L91.9016 91.383L83.5826 99.6934ZM92.1831 91.1011L92.3229 90.9613V90.9617L92.1831 91.1011Z"
            fill="white"
          />
          <path
            d="M16.0014 0.130437H0.644207L0.513428 -1.65105e-05H16.1319L16.0014 0.130437Z"
            fill="#F6F6F6"
          />
          <path
            d="M8.73977 8.22659L8.32207 7.80888L0.644287 0.130446H16.0015L8.32741 7.80454L8.74444 8.22158L8.73977 8.22659Z"
            fill="#F6F6F6"
          />
          <path
            d="M17.2814 0.130437H16.0016L16.132 -1.65105e-05H17.1509L17.2814 0.130437Z"
            fill="#F7F7F7"
          />
          <path
            d="M16.6416 16.1196L8.74431 8.22158L8.32727 7.80455L16.0014 0.130453H17.2812L24.952 7.80888L16.6416 16.1196Z"
            fill="#F7F7F7"
          />
          <path
            d="M25.3692 24.8549L25.3055 24.7912L25.3695 24.8549H25.3692ZM24.9562 24.4419L24.9525 24.4383L16.6422 16.1279L16.6378 16.1236L16.6418 16.1196L24.9522 7.80889L25.3692 8.22661L25.3739 8.22191L33.2782 16.1199L33.2748 16.1236L33.2785 16.1272L24.9562 24.4419Z"
            fill="#F7F7F7"
          />
          <path
            d="M33.2824 32.7533L33.2817 32.7522L25.3694 24.8549L25.3053 24.7912L24.9557 24.4422L24.956 24.4419L33.2784 16.1272L41.5894 24.4382L33.2824 32.7533Z"
            fill="#F7F7F7"
          />
          <path
            d="M42.0062 41.4926L41.5928 41.0793L33.2748 32.7609L33.2825 32.7533L41.5895 24.4383L41.5935 24.4422L42.0062 24.855L49.9118 32.7523L41.5935 41.0706L42.0108 41.488L42.0062 41.4926Z"
            fill="#F7F7F7"
          />
          <path
            d="M49.9078 49.3856L42.0109 41.488L41.5935 41.0706L49.9118 32.7522L49.9122 32.7526L49.9038 32.7609L58.2302 41.0793L49.9078 49.3856Z"
            fill="#F7F7F7"
          />
          <path
            d="M58.2257 57.6956L49.908 49.394L49.9037 49.3896L49.9077 49.3856L58.23 41.0793L58.3615 41.2107L66.5324 49.3893L66.532 49.3896L66.5327 49.3903L58.2257 57.6956Z"
            fill="#FCFCFC"
          />
          <path
            d="M66.5332 66.0179L58.2265 57.7039L58.2222 57.6996L58.2262 57.6956L66.5332 49.3903L66.7837 49.6405L66.9459 49.803L74.8505 57.6999L66.5332 66.0179Z"
            fill="#FEFFFF"
          />
          <path
            d="M74.855 74.333L74.8507 74.3286L66.5323 66.0186L66.533 66.0179L74.8504 57.6999L75.2641 58.1133L75.2644 58.1129L83.1697 66.0183L74.855 74.333Z"
            fill="#FEFFFF"
          />
          <path
            d="M83.5826 83.069L83.1655 82.652L74.8508 74.337L74.8548 74.333L83.1695 66.0183L83.5826 66.4313L91.4882 74.3286L83.1698 82.647L83.5872 83.0647L83.5826 83.069Z"
            fill="#FCFCFC"
          />
          <path
            d="M91.9015 91.383L83.5872 83.0647L83.1698 82.647L91.4881 74.3286L91.4921 74.3326L91.4881 74.337L99.7982 82.647L100.216 83.0647L92.3229 90.9613L92.1831 91.1011L91.9015 91.383Z"
            fill="#FEFFFF"
          />
          <path
            d="M32.6368 0.130437H17.2812L17.1508 -1.65105e-05H32.7676L32.6368 0.130437Z"
            fill="#ECECEB"
          />
          <path
            d="M25.3691 8.22659L24.952 7.80888L17.2812 0.130446H32.6368L24.9557 7.80454L25.3737 8.2219L25.3691 8.22659Z"
            fill="#ECECEB"
          />
          <path
            d="M33.9183 0.130437H32.6368L32.7676 -1.65105e-05H33.7879L33.9183 0.130437Z"
            fill="#F6F6F6"
          />
          <path
            d="M33.278 16.1199L25.3737 8.22191L24.9557 7.80455L32.6368 0.130453H33.9183L41.589 7.80888L33.278 16.1199Z"
            fill="#F6F6F6"
          />
          <path
            d="M42.0062 24.8549L41.5935 24.4422L42.0065 24.8549H42.0062ZM41.5935 24.4422L41.5895 24.4383L33.2784 16.1272L33.2748 16.1236L33.2781 16.1199L41.5891 7.80889L42.0062 8.22661L42.0108 8.22191L49.9078 16.1192L49.9038 16.1236L49.9081 16.1279L41.5935 24.4422Z"
            fill="#EAEBE9"
          />
          <path
            d="M49.9122 32.7526L49.9118 32.7522L42.0065 24.8549L41.5935 24.4422L49.9082 16.1279L58.2265 24.4379L58.2219 24.4422L58.2222 24.4426L49.9122 32.7526Z"
            fill="#F6F5F5"
          />
          <path
            d="M58.3615 41.2107L58.23 41.0793L49.9037 32.7609L49.912 32.7526L58.222 24.4426L66.5327 32.7522L58.2217 41.0706L58.3615 41.2107Z"
            fill="#F9FAFA"
          />
          <path
            d="M66.9459 49.803L66.7837 49.6405L66.9462 49.803H66.9459ZM66.5328 49.3893L58.362 41.2107L58.2222 41.0706L66.5332 32.7522L66.5372 32.7566L66.5325 32.7609L74.8509 41.0793L66.5325 49.3896"
            fill="#F7F7F7"
          />
          <path
            d="M75.2641 58.1133L74.8504 57.6999L66.946 49.8029L66.7836 49.6404L66.533 49.3902L66.5323 49.3896L74.8507 41.0792L75.2641 41.4926L75.2687 41.4882L83.1697 49.3899L74.851 57.6996L75.2644 58.1129L75.2641 58.1133Z"
            fill="#FBFBFA"
          />
          <path
            d="M83.5825 66.4313L83.1695 66.0183L75.2642 58.113L74.8508 57.6996L83.1695 49.39L83.5825 49.803L83.5832 49.8023L91.4879 57.7L83.1698 66.0186L83.5829 66.4313H83.5825Z"
            fill="#FCFCFC"
          />
          <path
            d="M91.4921 74.3326L91.4881 74.3286L83.5828 66.4313L83.1698 66.0186L91.4878 57.6999L91.9015 58.1133L99.8028 66.0139L99.7982 66.0186L99.8025 66.023L91.4921 74.3326Z"
            fill="#FBFBFA"
          />
          <path
            d="M99.7982 82.647L91.4882 74.337L91.4922 74.3326L99.8025 66.023L108.117 74.3286L99.7982 82.647Z"
            fill="#FBFBFA"
          />
          <path
            d="M108.53 91.383L100.216 83.0647L99.7982 82.647L108.117 74.3286L108.121 74.333L108.117 74.337L116.432 82.652L108.117 90.9617L108.534 91.3787L108.53 91.383Z"
            fill="white"
          />
          <path
            d="M116.849 99.6934L108.534 91.3787L108.117 90.9617L116.432 82.652L116.849 83.069L116.853 83.0647L125.163 91.3787L116.849 99.6934Z"
            fill="white"
          />
          <path
            d="M49.2675 0.130437H33.9183L33.7878 -1.65105e-05H49.398L49.2675 0.130437Z"
            fill="#EFEFEF"
          />
          <path
            d="M42.0061 8.22659L41.5891 7.80888L33.9183 0.130446H49.2676L41.5934 7.80454L42.0108 8.2219L42.0061 8.22659Z"
            fill="#EFEFEF"
          />
          <path
            d="M50.548 0.130437H49.2676L49.398 -1.65105e-05H50.4173L50.548 0.130437Z"
            fill="#EAEAE9"
          />
          <path
            d="M49.9078 16.1192L42.0109 8.2219L41.5935 7.80454L49.2676 0.130447H50.5481L58.2219 7.80454L58.2265 7.8092L49.9078 16.1192Z"
            fill="#EAEBE9"
          />
          <path
            d="M58.2264 24.4379L49.908 16.1279L49.9037 16.1236L49.9077 16.1192L58.2264 7.8092L66.5324 16.1232L66.532 16.1236L66.5327 16.1239L58.2264 24.4379Z"
            fill="#E6E5E3"
          />
          <path
            d="M66.5372 32.7566L66.5332 32.7522L58.2225 24.4426L58.2222 24.4422L58.2268 24.4379L66.5332 16.1239L74.8472 24.4382L66.5372 32.7566Z"
            fill="#ECECEB"
          />
          <path
            d="M75.2641 41.4926L74.8507 41.0793L66.5323 32.7609L66.537 32.7566L74.847 24.4383L74.8514 24.4422H74.851L74.9234 24.5143L75.2641 24.855L83.1694 32.7523L74.851 41.0706L75.2687 41.4883L75.2641 41.4926Z"
            fill="#F7F7F7"
          />
          <path
            d="M83.5826 49.803L83.1695 49.3899L75.2685 41.4883L74.8508 41.0706L83.1692 32.7522L83.1739 32.7569L83.1698 32.7609L91.4882 41.0793L83.1698 49.3896L83.5832 49.8023L83.5826 49.803Z"
            fill="#F3F2F2"
          />
          <path
            d="M91.9015 58.1133L91.4878 57.6999L83.5832 49.8023L83.1698 49.3896L91.4881 41.0792L91.9015 41.4926L91.9059 41.4882L99.8025 49.3852L99.7982 49.3896L99.8028 49.3942L91.4881 57.6996L91.9015 58.1133Z"
            fill="#FBFBFA"
          />
          <path
            d="M99.8028 66.014L91.9015 58.1133L91.4882 57.6996L99.8028 49.3943L108.116 57.6999L99.8028 66.014Z"
            fill="#FBFBFA"
          />
          <path
            d="M108.121 74.333L108.117 74.3286L99.8026 66.0229L99.7982 66.0186L99.8029 66.0139L108.116 57.6999L108.53 58.1133L108.53 58.1129L116.436 66.0183L108.121 74.333Z"
            fill="#FEFFFF"
          />
          <path
            d="M116.849 83.069L116.432 82.652L108.117 74.337L108.121 74.333L116.436 66.0183L116.436 66.0186L116.849 66.4313L124.754 74.3286L116.436 82.647L116.853 83.0647L116.849 83.069Z"
            fill="#FEFFFF"
          />
          <path
            d="M125.167 91.383L125.163 91.3787L116.853 83.0647L116.436 82.647L124.754 74.3286L124.758 74.3326L124.754 74.337L133.482 83.065L125.167 91.383Z"
            fill="white"
          />
          <path
            d="M65.889 0.130437H50.5481L50.4174 -1.65105e-05H66.0195L65.889 0.130437Z"
            fill="#ECECEB"
          />
          <path
            d="M58.2218 7.80455L50.548 0.130456H65.8889L58.2218 7.80455Z"
            fill="#ECECEB"
          />
          <path
            d="M67.1757 0.130437H65.8889L66.0194 -1.65105e-05H67.0453L67.1757 0.130437Z"
            fill="#F2F2F1"
          />
          <path
            d="M66.5328 16.1232L58.2268 7.80921L58.2222 7.80455L65.8893 0.130453H67.1761L74.8469 7.80888L66.5325 16.1236"
            fill="#F1F2F2"
          />
          <path
            d="M74.8514 24.4422L74.847 24.4383L66.533 16.1239L66.5323 16.1236L74.8467 7.80889L75.2641 8.22661L75.2687 8.22191L83.1697 16.1236L74.851 24.4422"
            fill="#F1F2F2"
          />
          <path
            d="M83.1739 32.7569L83.1692 32.7522L75.2639 24.8549L74.9232 24.5143L74.8508 24.4422L83.1695 16.1236L83.5826 16.5369L83.5829 16.5366L91.4845 24.4382L83.1739 32.7569Z"
            fill="#F0F1F1"
          />
          <path
            d="M91.9015 41.4926L91.4881 41.0793L83.1698 32.7609L83.1738 32.7569L91.4845 24.4383L91.4885 24.4419L91.4881 24.4422L99.8068 32.7523L91.4881 41.0706L91.9059 41.4883L91.9015 41.4926Z"
            fill="#FAFAFA"
          />
          <path
            d="M99.8025 49.3853L91.9059 41.4883L91.4882 41.0706L99.8068 32.7522L99.7982 32.7609L108.117 41.0793L99.8025 49.3853Z"
            fill="#FAFAFA"
          />
          <path
            d="M108.53 58.1133L108.116 57.6999L99.8029 49.3942L99.7982 49.3896L99.8026 49.3852L108.117 41.0792L108.53 41.4926L108.535 41.4882L116.436 49.3899L108.117 57.6996L108.53 58.1129L108.53 58.1133Z"
            fill="#F7F7F7"
          />
          <path
            d="M116.436 66.0186L116.436 66.0183L108.53 58.113L108.117 57.6996L116.436 49.39L116.849 49.803L116.849 49.8027L124.754 57.7L116.436 66.0186Z"
            fill="#F1F2F2"
          />
          <path
            d="M124.758 74.3326L124.754 74.3286L116.849 66.4313L116.436 66.0186L124.754 57.6999L125.167 58.1133L133.068 66.0143L133.064 66.0186L133.068 66.0226L124.758 74.3326Z"
            fill="#FAFAFA"
          />
          <path
            d="M133.486 83.069L133.481 83.065L124.754 74.337L124.758 74.3327L133.068 66.0226L141.383 74.3286L133.064 82.647L133.486 83.0687V83.069Z"
            fill="#FAFAFA"
          />
          <path
            d="M141.796 91.383L133.486 83.0687L133.065 82.647L141.383 74.3286L141.387 74.3333L141.384 74.337L144.385 77.3356V88.7951L141.796 91.383Z"
            fill="white"
          />
          <path
            d="M82.525 0.130437H67.1757L67.0453 -1.65105e-05H82.6554L82.525 0.130437Z"
            fill="#E5E5E3"
          />
          <path
            d="M75.2639 8.22659L74.8466 7.80888L67.1758 0.130446H82.525L74.8509 7.80454L75.2686 8.2219L75.2639 8.22659Z"
            fill="#E5E4E3"
          />
          <path
            d="M83.8135 0.130437H82.525L82.6555 -1.65105e-05H83.683L83.8135 0.130437Z"
            fill="#F2F2F1"
          />
          <path
            d="M83.5825 16.5369L83.1695 16.1235L75.2685 8.22185L74.8508 7.80449L82.5249 0.130396H83.8134L91.4842 7.80883L83.1698 16.1235L83.5829 16.5365L83.5825 16.5369Z"
            fill="#F1F2F2"
          />
          <path
            d="M91.4885 24.4419L91.4845 24.4382L83.5828 16.5366L83.1698 16.1235L91.4841 7.80886L91.9015 8.22659L91.9059 8.22225L99.8025 16.1192L99.7982 16.1235L99.8028 16.1279L91.4885 24.4419Z"
            fill="#F0F1F1"
          />
          <path
            d="M99.8068 32.7522L91.4882 24.4422L91.4885 24.4419L99.8028 16.1279L108.113 24.4382L99.8068 32.7522Z"
            fill="#F6F5F5"
          />
          <path
            d="M108.53 41.4926L108.117 41.0793L99.7982 32.7609L99.8069 32.7523L108.113 24.4383L108.117 24.4422L108.189 24.514L108.53 24.855L116.436 32.7523L108.117 41.0706L108.535 41.4883L108.53 41.4926Z"
            fill="#F9FAFA"
          />
          <path
            d="M116.849 49.803L116.436 49.3899L108.535 41.4883L108.117 41.0706L116.436 32.7522L116.44 32.7569L116.436 32.7609L124.754 41.0793L116.436 49.3896L116.849 49.8026L116.849 49.803Z"
            fill="#F0F1F1"
          />
          <path
            d="M125.167 58.1133L124.754 57.6999L116.849 49.8026L116.436 49.3896L124.754 41.0792L125.167 41.4926L125.172 41.4882L133.068 49.3855L133.064 49.3896L133.069 49.3939L124.754 57.6996L125.167 58.1129V58.1133Z"
            fill="#F1F2F2"
          />
          <path
            d="M133.068 66.0143L125.167 58.113L124.754 57.6996L133.069 49.3939L141.382 57.6999L133.068 66.0143Z"
            fill="#F0F1F1"
          />
          <path
            d="M141.387 74.3333L141.383 74.3286L133.068 66.0226L133.065 66.0186L133.069 66.0143L141.383 57.6999L141.796 58.1133L141.797 58.1123L144.385 60.6983V71.3383L141.387 74.3333Z"
            fill="#FCFCFC"
          />
          <path
            d="M144.385 77.3357L141.384 74.337L141.387 74.3333L144.385 71.3384V77.3357Z"
            fill="#FDFDFD"
          />
          <path
            d="M99.1625 0.130437H83.8133L83.6829 -1.65105e-05H99.293L99.1625 0.130437Z"
            fill="#E9E9E8"
          />
          <path
            d="M91.9015 8.22659L91.4841 7.80888L83.8134 0.130446H99.1626L91.4881 7.80454L91.9058 8.22226L91.9015 8.22659Z"
            fill="#E9E9E8"
          />
          <path
            d="M100.442 0.130437H99.1626L99.2931 -1.65105e-05H100.311L100.442 0.130437Z"
            fill="#F1F1F1"
          />
          <path
            d="M99.8025 16.1192L91.9059 8.22226L91.4882 7.80454L99.1626 0.130447H100.442L108.113 7.80888L99.8025 16.1192Z"
            fill="#F0F1F1"
          />
          <path
            d="M108.53 24.8549L108.189 24.514L108.53 24.8549H108.53ZM108.117 24.4422L108.113 24.4383L99.8029 16.1279L99.7982 16.1236L99.8026 16.1192L108.113 7.80889L108.53 8.22661L108.535 8.22191L116.436 16.1236L108.117 24.4422Z"
            fill="#F4F4F3"
          />
          <path
            d="M116.44 32.7569L116.436 32.7522L108.53 24.8549L108.189 24.514L108.117 24.4422L116.436 16.1236L116.849 16.5369L116.849 16.5366L124.751 24.4382L116.44 32.7569Z"
            fill="#F3F2F2"
          />
          <path
            d="M125.167 41.4926L124.754 41.0793L116.436 32.7609L116.44 32.7569L124.75 24.4383L124.754 24.4419L124.754 24.4422L133.072 32.7523L124.754 41.0706L125.172 41.4883L125.167 41.4926Z"
            fill="#F7F7F7"
          />
          <path
            d="M133.068 49.3856L125.172 41.4883L124.754 41.0706L133.072 32.7522L133.073 32.7526L133.064 32.7609L141.383 41.0793L133.068 49.3856Z"
            fill="#F7F7F7"
          />
          <path
            d="M141.796 58.1133L141.383 57.6999L133.069 49.3939L133.065 49.3896L133.068 49.3855L141.383 41.0792L141.796 41.4926L141.801 41.4876L144.385 44.0695V54.7042L141.384 57.6996L141.797 58.1122L141.796 58.1133Z"
            fill="#F1F2F2"
          />
          <path
            d="M144.385 60.6983L141.797 58.1123L141.384 57.6997L144.385 54.7043V60.6983Z"
            fill="#F4F4F3"
          />
          <path
            d="M115.791 0.130437H100.442L100.311 -1.65105e-05H115.922L115.791 0.130437Z"
            fill="#F1F1F1"
          />
          <path
            d="M108.53 8.22659L108.113 7.80888L100.442 0.130446H115.791L108.117 7.80454L108.535 8.2219L108.53 8.22659Z"
            fill="#F0F1F1"
          />
          <path
            d="M117.079 0.130437H115.791L115.922 -1.65105e-05H116.949L117.079 0.130437Z"
            fill="#F7F7F7"
          />
          <path
            d="M116.849 16.5369L116.436 16.1235L108.535 8.22185L108.117 7.80449L115.791 0.130396H117.08L124.75 7.80883L116.436 16.1235L116.849 16.5365L116.849 16.5369Z"
            fill="#F7F7F7"
          />
          <path
            d="M124.754 24.4419L124.75 24.4382L116.849 16.5366L116.436 16.1235L124.75 7.80886L125.167 8.22659L125.172 8.22225L133.068 16.1195L133.064 16.1235L133.068 16.1276L124.754 24.4419Z"
            fill="#F1F2F2"
          />
          <path
            d="M133.073 32.7526L133.072 32.7522L124.754 24.4422L124.754 24.4419L133.068 16.1276L141.379 24.4382L133.073 32.7526Z"
            fill="#F0F0F0"
          />
          <path
            d="M141.796 41.4926L141.383 41.0793L133.065 32.7609L133.073 32.7526L141.379 24.4383L141.796 24.855L141.797 24.8543L144.385 27.4379V38.0723L141.384 41.0706L141.801 41.4876L141.796 41.4926Z"
            fill="#F0F1F1"
          />
          <path
            d="M144.385 44.0695L141.801 41.4876L141.384 41.0706L144.385 38.0722V44.0695Z"
            fill="#F3F2F2"
          />
          <path
            d="M132.428 0.130437H117.079L116.949 -1.65105e-05H132.559L132.428 0.130437Z"
            fill="#DFDFDD"
          />
          <path
            d="M125.167 8.22659L124.75 7.80888L117.079 0.130446H132.428L124.754 7.80454L125.172 8.22226L125.167 8.22659Z"
            fill="#DFDFDD"
          />
          <path
            d="M133.708 0.130432H132.428L132.559 -2.16365e-05H133.578L133.708 0.130432Z"
            fill="#F2F2F1"
          />
          <path
            d="M133.068 16.1196L125.172 8.22227L124.754 7.80455L132.428 0.130453H133.708L141.379 7.80888L133.068 16.1196Z"
            fill="#F1F2F2"
          />
          <path
            d="M141.796 24.8549L141.379 24.4383L133.069 16.1276L133.065 16.1236L133.068 16.1196L141.379 7.80889L141.796 8.22661L141.801 8.22126L144.385 10.8035V21.4436L141.384 24.4422L141.797 24.8543L141.796 24.8549Z"
            fill="#F4F4F3"
          />
          <path
            d="M144.385 27.4379L141.797 24.8543L141.384 24.4422L144.385 21.4436V27.4379Z"
            fill="#D8D7D5"
          />
          <path
            d="M144.385 4.80589V0.130441H133.708L133.578 -1.33514e-05H144.385V4.80589Z"
            fill="#EFEFEF"
          />
          <path
            d="M141.796 8.22659L141.379 7.80888L133.708 0.130446H144.385V4.8059L141.384 7.80454L141.801 8.22125L141.796 8.22659Z"
            fill="#EFEFEF"
          />
          <path
            d="M144.385 10.8035L141.801 8.22125L141.384 7.80454L144.385 4.8059V10.8035Z"
            fill="#F6F6F6"
          />
        </g>
        <defs>
          <clipPath id="clip0_3964_9317">
            <rect width="144.385" height="99.6934" fill="white" />
          </clipPath>
        </defs>
      </svg>

      <div
        className="position-relative d-flex flex-column pt-3"
        style={{
          minHeight: "100vh",
          zIndex: 1,
        }}
      >
        <div className="flex-grow-1 mx-3 overflow-auto overflow-x-hidden">
          {selectedTab === "1" && (
            <>
              <SenderAndBillingDetails
                isBillingDetails={false}
                colorName={brandColor}
                companyDetails={companyDetails}
                referenceNumber={referenceNumber}
                quoteName={quoteTitle || ""}
                isCompanyPhoneHidden={isCompanyPhoneHidden}
                isCompanyNameHidden={isCompanyNameHidden}
              />
              <hr className="my-4 text-greyE4 border-2" />
              {clientDetails && (
                <SenderAndBillingDetails
                  isBillingDetails={true}
                  colorName={brandColor}
                  clientDetails={clientDetails}
                  companyDetails={companyDetails}
                  issueDate={issueDate}
                  dueDate={dueDate}
                />
              )}

              {jobDescription && jobDescription?.length > 0 && (
                <>
                  <div className="fw-medium text-black2d font-primary mb-2 mt-4">
                    <span style={{ color: brandColor }}> Job Description</span>
                  </div>
                  <p
                    className="text-black2D font-primary fw-normal fs-9"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {jobDescription}
                  </p>
                </>
              )}
            </>
          )}
          {/* TABLE AREA */}
          {selectedTab === "2" && (
            <>
              <div className="fw-medium text-black2d font-primary mt-4">
                <span style={{ color: brandColor }}>Items Summary</span>
              </div>
              <hr className="text-greyE4 border-2" />
              {(mainTableData.length > 0 || allViewData.length > 0) && (
                <>
                  {selectedOption === "by-category-subcategory-item" ? (
                    <>
                      {allViewData.map((cat) => (
                        <div key={cat.categoryId} className="mt-4">
                          {/* Category heading like screenshot (MATERIAL, SERVICES, etc.) */}
                          <div
                            className="fw-semibold text-uppercase fs-14 "
                            style={{ color: brandColor }}
                          >
                            {cat.category}
                          </div>

                          <ItemsTable
                            colorName={brandColor}
                            columns={columns}
                            data={cat.rows}
                          />
                        </div>
                      ))}
                    </>
                  ) : (
                    <div>
                      <ItemsTable
                        colorName={brandColor}
                        columns={columns}
                        data={mainTableData}
                      />
                    </div>
                  )}
                  <InvoiceFooter colorName={brandColor} />
                </>
              )}
            </>
          )}

          {selectedTab === "3" && (
            <>
              <div className="fw-medium text-black2d font-primary mt-3">
                <span style={{ color: brandColor }}>Quote Summary</span>
              </div>
              <hr className="text-greyE4 border-2" />
              {allSelectedSections &&
                allSelectedSections.length > 0 &&
                allSelectedSections.map((section, index) => (
                  <div key={section.id} className="mb-4">
                    <div
                      className="fw-semibold text-uppercase fs-14 mb-2"
                      style={{ color: brandColor }}
                    >
                      {index + 1}. {section.title}
                    </div>
                    <div className="html-content">
                      {parse(section.content) ?? "-"}
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>

        {/* Bottom SVG */}
        <svg
          className="w-100 mt-auto br-10"
          viewBox="0 0 468 190"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ flexShrink: 0 }}
        >
          <path
            d="M0 162C0 162 415.373 140.056 468 0V190H0V162Z"
            fill="#F9F9FA"
          />
          <path
            d="M0 162C0 162 415.373 145.056 468 55V190H0V162Z"
            fill={brandColor}
          />
        </svg>
      </div>
    </Col>
  );
};

export default memo(TemplateFirst);
